import { Injectable } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { locale, loadMessages } from 'devextreme/localization';
// import DevExtreme translation message files
import * as caMessages from 'assets/i18n-devextreme/ca.json';
import * as esMessages from 'devextreme/localization/messages/es.json';
import * as frMessages from 'devextreme/localization/messages/fr.json';
import * as ruMessages from 'devextreme/localization/messages/ru.json';
import * as deMessages from 'devextreme/localization/messages/de.json';
import * as ltMessages from 'assets/i18n-devextreme/lt.json';
import * as zhMessages from 'devextreme/localization/messages/zh.json';
import { LocalStorageService } from '../localStorage/local-storage.service';

export class TranslationInstantParams {
  label: string;
  vars: any;

  constructor(label: string, vars = {}) {
    this.label = label;
    this.vars = vars;
  }
}

// Must return a function that returns void | Promise<any> | Observable<any>
export const translationMWInitializerFactory = (translationMWService: TranslationMWService) => () =>
  translationMWService.init();

@Injectable({ providedIn: 'root' })
export class TranslationMWService {
  constructor(
    private translateService: TranslateService,
    private localStorageService: LocalStorageService
  ) {
    // load DevExtreme translations
    loadMessages(caMessages);
    loadMessages(esMessages);
    loadMessages(frMessages);
    loadMessages(ruMessages);
    loadMessages(deMessages);
    loadMessages(ltMessages);
    loadMessages(zhMessages);
    // subscribe to language change
    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.localStorageService.currentLanguage = event.lang;
    });
  }

  /**
   * Called once at app startup via APP_INITIALIZER.
   * Angular will not finish bootstrapping until this Promise resolves.
   */
  init(): Promise<void> {
    const lang = this.localStorageService.currentLanguage;
    return new Promise<void>((resolve, reject) => {
      this.setTransLanguage(lang).subscribe({
        next: () => {
          // no-op, we just care that it starts and completes
        },
        error: (err) => {
          reject(err);
        },
        complete: () => {
          // by this point, ngx-translate has loaded the language
          resolve();
        },
      });
    });
  }

  public instant(label: string, vars = {}): string {
    return this.translateService.instant(label, vars) as string;
  }

  public setTransLanguage(lang: string) {
    locale(lang);
    return this.translateService.use(lang);
  }

  public get currentLanguage(): string {
    if (!this.translateService.currentLang) {
      this.setTransLanguage(this.localStorageService.currentLanguage);
    }
    return this.translateService.currentLang;
  }
}
